/**
 * Debug toggle controller for the /help/contact page.
 *
 * WHAT THIS FILE DOES:
 * - Renders the extension debug toggle panel inside the help form.
 * - Talks to the extension via externally_connectable messages.
 * - Handles consent before enabling debug logging.
 *
 * WHY THIS EXISTS:
 * - Keeps debug toggle logic out of the main support form controller.
 * - Encapsulates Chrome extension messaging concerns in a focused module.
 * - Avoids a "god module" as the help UI grows.
 *
 * ARCHITECTURE CONTEXT:
 * - Uses extension-debug.ts for message transport.
 * - UI is injected into support-form-controller.ts.
 * - Extension side lives in background/core/router.js.
 *
 * RELATED FILES:
 * - packages/backend/src/client/help-contact/extension-debug.ts
 * - packages/chrome-plugin/extension/background/core/router.js
 * - packages/chrome-plugin/extension/manifest.json
 *
 * NON-GOALS:
 * - No automatic install flows (we only show a CTA link).
 * - No analytics tracking (handled elsewhere).
 */

import { createElement } from './dom.js';
import { fetchDebugMode, setDebugMode, type DebugMode, type DebugStatus } from './extension-debug.js';

interface DebugElements {
  container: HTMLElement;
  statusDot: HTMLElement;
  statusText: HTMLElement;
  versionText: HTMLElement;
  toggleInput: HTMLInputElement;
  consentPanel: HTMLElement;
  consentConfirm: HTMLButtonElement;
  consentCancel: HTMLButtonElement;
  installCta: HTMLAnchorElement;
}

interface DebugToggleOptions {
  onError: (message: string) => void;
}

const WEB_STORE_URL = 'https://chromewebstore.google.com/detail/gist/iilleobakpmdhineleplnjifbpoiioga';

/**
 * Controller for the debug toggle panel.
 */
export class DebugToggleController {
  private elements: DebugElements | null = null;
  private debugStatus: DebugStatus | null = null;
  private consentGranted: boolean = false;
  private onError: (message: string) => void;

  constructor(options: DebugToggleOptions) {
    this.onError = options.onError;
  }

  /**
   * Render the debug toggle panel and start status refresh.
   */
  render(): HTMLElement {
    const container = createElement('section', { class: 'help-debug', 'aria-live': 'polite' });

    const header = createElement('div', { class: 'help-debug__header' });
    header.appendChild(createElement('h2', { class: 'help-debug__title' }, 'Extension diagnostics'));
    header.appendChild(createElement('p', { class: 'help-debug__subtitle' }, 'Enable debug logging to help us troubleshoot your issue. Logs are sent only while debug is on.'));
    container.appendChild(header);

    const statusRow = createElement('div', { class: 'help-debug__status-row' });
    const statusLeft = createElement('div', { class: 'help-debug__status-left' });
    const statusDot = createElement('span', { class: 'help-debug__dot help-debug__dot--pending', 'aria-hidden': 'true' });
    const statusText = createElement('span', { class: 'help-debug__status-text' }, 'Checking for extension...');
    statusLeft.appendChild(statusDot);
    statusLeft.appendChild(statusText);

    const versionText = createElement('span', { class: 'help-debug__version' }, '');
    statusLeft.appendChild(versionText);
    statusRow.appendChild(statusLeft);

    const toggleWrap = createElement('label', { class: 'help-debug__toggle' });
    const toggleInput = createElement('input', { id: 'help-debug-toggle', type: 'checkbox', class: 'help-toggle-input', disabled: true });
    const togglePill = createElement('span', { class: 'help-toggle-pill', 'aria-hidden': 'true' });
    toggleWrap.appendChild(toggleInput);
    toggleWrap.appendChild(togglePill);
    statusRow.appendChild(toggleWrap);
    container.appendChild(statusRow);

    const installCta = createElement('a', {
      class: 'help-debug__cta',
      href: WEB_STORE_URL,
      target: '_blank',
      rel: 'noopener noreferrer'
    }, 'Install the Gist extension');
    installCta.hidden = true;
    container.appendChild(installCta);

    const consentPanel = createElement('div', { id: 'help-debug-consent', class: 'help-debug-consent', hidden: true });
    consentPanel.appendChild(createElement('p', { class: 'help-debug-consent__copy' }, 'Enabling debug mode sends additional diagnostic logs to our servers. Use it only while troubleshooting.'));
    const consentActions = createElement('div', { class: 'help-debug-consent__actions' });
    const consentConfirm = createElement('button', { id: 'help-debug-consent-confirm', type: 'button', class: 'help-btn help-btn--primary' }, 'Enable debug');
    const consentCancel = createElement('button', { id: 'help-debug-consent-cancel', type: 'button', class: 'help-btn help-btn--ghost' }, 'Cancel');
    consentActions.appendChild(consentConfirm);
    consentActions.appendChild(consentCancel);
    consentPanel.appendChild(consentActions);
    container.appendChild(consentPanel);

    this.elements = {
      container,
      statusDot,
      statusText,
      versionText,
      toggleInput,
      consentPanel,
      consentConfirm,
      consentCancel,
      installCta
    };

    this.bindEvents();
    void this.refreshStatus();

    return container;
  }

  /**
   * No-op for now; placeholder for future teardown.
   */
  dispose(): void {
    // No active intervals or event listeners to remove yet.
  }

  private bindEvents(): void {
    if (!this.elements) return;
    const { toggleInput, consentPanel, consentConfirm, consentCancel } = this.elements;

    toggleInput.addEventListener('change', () => {
      if (toggleInput.checked) {
        toggleInput.checked = false;
        if (!this.consentGranted) {
          consentPanel.hidden = false;
          return;
        }
        void this.applyMode('debug');
      } else {
        void this.applyMode('off');
      }
    });

    consentConfirm.addEventListener('click', () => {
      consentPanel.hidden = true;
      this.consentGranted = true;
      void this.applyMode('debug');
    });

    consentCancel.addEventListener('click', () => {
      consentPanel.hidden = true;
    });
  }

  private async refreshStatus(): Promise<void> {
    if (!this.elements) return;
    const { statusText, statusDot, toggleInput, installCta, versionText } = this.elements;

    statusText.textContent = 'Checking for extension...';
    statusDot.className = 'help-debug__dot help-debug__dot--pending';
    toggleInput.disabled = true;
    toggleInput.checked = false;
    installCta.hidden = true;
    versionText.textContent = '';

    const status = await fetchDebugMode();
    this.debugStatus = status;
    this.updateUi(status);
  }

  private updateUi(status: DebugStatus): void {
    if (!this.elements) return;
    const { statusText, statusDot, toggleInput, installCta, versionText } = this.elements;

    if (status.state === 'available') {
      const isOn = status.loggingMode !== 'off';
      statusText.textContent = isOn ? 'Debug logging enabled' : 'Extension detected';
      statusDot.className = `help-debug__dot ${isOn ? 'help-debug__dot--active' : 'help-debug__dot--ok'}`;
      toggleInput.disabled = false;
      toggleInput.checked = isOn;
      installCta.hidden = true;
      versionText.textContent = status.extensionVersion ? `v${status.extensionVersion}` : '';
      return;
    }

    if (status.state === 'missing') {
      statusText.textContent = 'Extension not detected';
      statusDot.className = 'help-debug__dot help-debug__dot--warn';
      toggleInput.disabled = true;
      toggleInput.checked = false;
      installCta.hidden = false;
      versionText.textContent = '';
      return;
    }

    statusText.textContent = 'Unable to reach the extension';
    statusDot.className = 'help-debug__dot help-debug__dot--error';
    toggleInput.disabled = true;
    toggleInput.checked = false;
    installCta.hidden = false;
    versionText.textContent = '';
  }

  private async applyMode(mode: DebugMode): Promise<void> {
    if (!this.elements) return;
    const { toggleInput } = this.elements;
    toggleInput.disabled = true;

    const status = await setDebugMode(mode);
    this.debugStatus = status;
    this.updateUi(status);

    if (status.state === 'error') {
      this.onError(status.error);
    }
  }
}
